import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { ApiNotice } from 'api/types';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import Link from 'components/basic/Link/Link';
import RouterLink from 'components/basic/Link/RouterLink';
import * as types from 'lib/css/bgTypes';

type BannerNoticeWrapperProps = {
  $styleType: keyof typeof types;
  children: ReactNode;
  $isTopNotification?: boolean;
  $isDismissable?: boolean;
};

const NoticeContainer = styled.div`
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

export const LinkSpan = styled.span`
  margin-left: 5px;
  font-weight: bold;
`;

const NoticeWrapper = styled.div<BannerNoticeWrapperProps>`
  position: relative;
  display: flex;
  align-items: center;
  font-size: var(--medium);

  width: 100%;
  min-height: ${props => (props.$isTopNotification ? '33px' : '35px')};
  z-index: ${props => (props.$isDismissable === false ? '99999' : 'initial')};

  > strong {
    margin-right: 3px;
  }

  ${props => {
    // eslint-disable-next-line import/namespace
    return types[props.$styleType];
  }};

  ${props => (props.$isTopNotification ? 'font-size: var(--small);' : '')};

  .isMobileStyle & {
    padding: 7px;
  }
`;

const CloseIconStyled = styled(MaterialSymbol)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  margin-left: 10px;
  height: 100%;
  opacity: 0.8;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;

export default function Banner({
  notice,
  isTopNotification,
  children,
  onClose,
}: {
  notice: Pick<
    ApiNotice,
    'link' | 'linkText' | 'type' | 'message' | 'isDismissable'
  >;
  isTopNotification?: boolean;
  children?: ReactNode;
  onClose?: () => void;
}) {
  const isInAppRouterLink = notice.link?.startsWith('/');
  return (
    <NoticeWrapper
      $styleType={notice.type}
      $isTopNotification={isTopNotification}
      $isDismissable={notice.isDismissable}
    >
      <NoticeContainer>
        <div>
          {children}
          {notice.message}
          {notice.link && (
            <LinkSpan>
              {isInAppRouterLink ? (
                <RouterLink to={notice.link} color="inherit" underline>
                  {notice.linkText || 'Learn More'}
                </RouterLink>
              ) : (
                <Link href={notice.link} color="inherit" underline>
                  {notice.linkText || 'Learn More'}
                </Link>
              )}
            </LinkSpan>
          )}
        </div>
      </NoticeContainer>
      {notice.isDismissable && (
        <CloseIconStyled icon="close" onClick={onClose} fontSize="small" />
      )}
    </NoticeWrapper>
  );
}
